import { logger } from "@justhome/common";
import {
  Application,
  Lead,
  LeadCreationAction,
  LeadCreationResponse,
  Partner,
  usePostApplication,
  usePostLead,
} from "@justhome/api-core";
import { getFormConsentText } from "@justhome/react-ui";
import { fetchGetAssumptionsWithLogging } from "@lib/api-wrappers";
import useLanguage from "@lib/hooks/useLanguage";
import mapApplicationData from "@lib/mapApplicationData";
import { ApplicationFormValues } from "@lib/mapApplicationData/types";
import { localeToLanguageISOCode } from "@lib/utilities";
import { getOrigin } from "@lib/utm";
import { useCallback, useEffect, useRef, useState } from "react";
import { OptInFormData } from "../components/OptInForm";

interface UsePostApplicationWithLeadProps {
  onSuccess: (application: Application, lead: LeadCreationResponse) => void;
  applicationForm?: ApplicationFormValues;
  leadCreationAction: LeadCreationAction;
  partner?: Partner;
}

const usePostApplicationWithLead = ({
  applicationForm,
  onSuccess,
  leadCreationAction,
  partner,
}: UsePostApplicationWithLeadProps) => {
  const locale = useLanguage();
  const [form, setForm] = useState<Lead>();
  const saveLeadAfterApplication = useRef(false);
  const applicationRef = useRef<Application>(undefined);

  const {
    isLoading: isLeadLoading,
    isError: isLeadError,
    mutate: postLead,
  } = usePostLead({
    onSuccess(lead) {
      onSuccess(applicationRef.current!, lead);
    },
    onError(error) {
      logger.error("Error while posting a lead.", error);
    },
  });

  const saveLead = useCallback(
    async (optInForm: OptInFormData, applicationId?: string) => {
      const utmOrigin = getOrigin();
      const origin = utmOrigin ? [utmOrigin] : [];
      const consentText = getFormConsentText({
        locale: locale,
        partner,
      });
      const body: Lead = {
        email: optInForm.email,
        firstName: optInForm.firstName,
        lastName: optInForm.lastName,
        phone: optInForm.phone,
        language: localeToLanguageISOCode(locale),
        applicationId,
        origin,
        partnerId: partner?.id,
        action: leadCreationAction,
        wantsToSellAProperty: applicationForm?.wantsToSellAProperty
          ? "YES"
          : "NO",
        consentText,
      };
      postLead({ body });
    },
    [applicationForm, postLead, locale, leadCreationAction, partner],
  );

  const {
    mutate: postApplication,
    isLoading: isApplicationLoading,
    isError: isApplicationError,
  } = usePostApplication({
    onError(error, variables) {
      logger.error("Error while posting an application.", error, variables);
    },
    onSuccess: (application) => {
      applicationRef.current = application;
      if (saveLeadAfterApplication.current) {
        saveLead(form!, applicationRef.current.id);
      }
    },
  });

  useEffect(() => {
    if (!applicationForm) {
      return;
    }

    const saveApplication = async (form: ApplicationFormValues) => {
      const assumptions = await fetchGetAssumptionsWithLogging({
        queryParams: {
          zipCode: form.postalCode,
        },
      });
      const origin = getOrigin();

      const application = mapApplicationData(form, assumptions, origin);
      postApplication({
        body: { ...application, id: applicationRef.current?.id },
      });
    };

    saveApplication(applicationForm);
  }, [postApplication, applicationForm, locale]);

  const handleSubmit = useCallback(
    async (form: Lead) => {
      setForm(form);
      if (applicationRef.current) {
        saveLead(form, applicationRef.current.id);
      } else {
        saveLeadAfterApplication.current = true;
      }
    },
    [saveLead],
  );

  return {
    isLoading: (form && isApplicationLoading) || isLeadLoading,
    handleSubmit,
    isError: (form && isApplicationError) || isLeadError,
  };
};

export default usePostApplicationWithLead;
